import app from '../../forum/app';
import Modal, { IInternalModalAttrs } from '../../common/components/Modal';
import ForgotPasswordModal from './ForgotPasswordModal';
import SignUpModal from './SignUpModal';
import Button from '../../common/components/Button';
import LogInButtons from './LogInButtons';
import extractText from '../../common/utils/extractText';
import ItemList from '../../common/utils/ItemList';
import Stream from '../../common/utils/Stream';
import type Mithril from 'mithril';
import RequestError from '../../common/utils/RequestError';
import type { LoginParams } from '../../common/Session';
import AlertUtil from '../../forum/dianLiang/alertUtil';
import classList from '../../common/utils/classList';
import Alert from '../../common/components/Alert';

const sdkLoginErrorTranslations: Record<string, string> = {
  '-2': 'dl-response.forum.login.errors.user_not_found',
  '-19': 'dl-response.forum.login.errors.username_illegal',
  '-21': 'dl-response.forum.login.errors.username_exists',
  '-31': 'dl-response.forum.login.errors.password_error',
  '-105': 'dl-response.forum.login.errors.email_exists',
  '-106': 'dl-response.forum.login.errors.email_illegal',
};

export interface ILoginModalAttrs extends IInternalModalAttrs {
  identification?: string;
  password?: string;
  remember?: boolean;
  returnUrl?: string;
  targetUrl?: string;
}

export default class LogInModal<CustomAttrs extends ILoginModalAttrs = ILoginModalAttrs> extends Modal<CustomAttrs> {
  /**
   * The value of the identification input.
   */
  identification!: Stream<string>;
  /**
   * The value of the password input.
   */
  password!: Stream<string>;
  /**
   * The value of the remember me input.
   */
  remember!: Stream<boolean>;

  oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
    super.oninit(vnode);

    this.identification = Stream(this.attrs.identification || '');
    this.password = Stream(this.attrs.password || '');
    this.remember = Stream(!!this.attrs.remember);
  }

  className() {
    return 'LogInModal Modal--small';
  }

  title() {
    return app.translator.trans('core.forum.log_in.title');
  }

  content() {
    return [<div className="Modal-body">{this.body()}</div>, <div className="Modal-footer">{this.footer()}</div>];
  }

  body() {
    let pkgLogo = app.data.pkgLogo;
    // 将字符串映射转换为 JavaScript 对象
    const obj = JSON.parse(pkgLogo.pkgLogo);
    // 创建 Map 对象并初始化
    const map = new Map(Object.entries(obj));
    // 获取特定键的值
    let localePkgLogo = map.get(app.data.locale);
    return [<LogInButtons />,<img className='Login-title-img' src={localePkgLogo}></img>, <div className="Form Form--centered">{this.fields().toArray()}</div>];
  }
  view() {
    if (this.alertAttrs) {
      this.alertAttrs.dismissible = false;
    }

    return (
      <div className={classList('Modal modal-dialog fade', this.className())}>
        <div className="Modal-content">
            <div className="Modal-close App-backControl closeBtn">
              <Button
                icon="fas fa-minus minimize"
                onclick={() => {
                  if (this.attrs.returnUrl) {
                    window.location.href = this.attrs.returnUrl;
                  } else {
                    this.hide();
                  }
                }}
                className="Button Button--icon Button--link"
                aria-label={app.translator.trans('core.lib.modal.close')}
              />
            </div>


          <form onsubmit={this.onsubmit.bind(this)}>
            <div className="Modal-header">
              <h3 className="App-titleControl App-titleControl--text closeBtn">{this.title()}</h3>
            </div>

            {!!this.alertAttrs && (
              <div className="Modal-alert">
                <Alert {...this.alertAttrs} />
              </div>
            )}

            {this.content()}
          </form>
        </div>
      </div>
    );
  }

  fields() {
    const items = new ItemList();

    const identificationLabel = extractText(app.translator.trans('core.forum.log_in.username_or_email_placeholder'));
    const passwordLabel = extractText(app.translator.trans('core.forum.log_in.password_placeholder'));

    items.add(
      'identification',
      <div className="Form-group">
        <input
          className="FormControl"
          name="identification"
          type="text"
          placeholder={identificationLabel}
          aria-label={identificationLabel}
          bidi={this.identification}
          disabled={this.loading}
        />
      </div>,
      30
    );

    items.add(
      'password',
      <div className="Form-group">
        <input
          className="FormControl"
          name="password"
          type="password"
          autocomplete="current-password"
          placeholder={passwordLabel}
          aria-label={passwordLabel}
          bidi={this.password}
          disabled={this.loading}
        />
      </div>,
      20
    );

    items.add(
      'remember',
      <div className="Form-group">
        <div className='remember-me-btn'>
          <label className="checkbox">
            <input type="checkbox" bidi={this.remember} disabled={this.loading} />
            {app.translator.trans('core.forum.log_in.remember_me_label')}
          </label>
        </div>
      </div>,
      10
    );

    items.add(
      'submit',
      <div className="Form-group">
        <Button className="Button Button--primary Button--block" type="submit" loading={this.loading}>
          {app.translator.trans('core.forum.log_in.submit_button')}
        </Button>
      </div>,
      -10
    );
    items.add(
      'hint',
      <div className="Form-group hint">
        <span>{app.translator.trans('dl-response.forum.login.hint_text')}</span>
      </div>,
      -20
    );

    return items;
  }

  footer() {
    return (
      <>
        {/* <p className="LogInModal-forgotPassword">
          <a onclick={this.forgotPassword.bind(this)}>{app.translator.trans('core.forum.log_in.forgot_password_link')}</a>
        </p> */}
        {app.forum.attribute<boolean>('allowSignUp') && (
          <p className="LogInModal-signUp">{app.translator.trans('core.forum.log_in.sign_up_text', { a: <a onclick={this.signUp.bind(this)} /> })}</p>
        )}
      </>
    );
  }

  /**
   * Open the forgot password modal, prefilling it with an email if the user has
   * entered one.
   */
  forgotPassword() {
    const email = this.identification();
    const attrs = email.includes('@') ? { email } : undefined;

    app.modal.show(ForgotPasswordModal, attrs);
  }

  /**
   * Open the sign up modal, prefilling it with an email/username/password if
   * the user has entered one.
   */
  signUp() {
    const identification = this.identification();

    const attrs = {
      [identification.includes('@') ? 'email' : 'username']: identification,
    };

    app.modal.show(SignUpModal, attrs);
  }

  onready() {
    this.$('[name=' + (this.identification() ? 'password' : 'identification') + ']').trigger('select');
  }

  onsubmit(e: SubmitEvent) {
    e.preventDefault();
    this.loading = true;
    const data = this.loginParams();
    if (!data.identification) {
      this.loading = false;
      AlertUtil.onWarn(app, app.translator.trans('dl-response.forum.warn.username_is_null'));
      return;
    }
    if (!data.password) {
      this.loading = false;
      AlertUtil.onWarn(app, app.translator.trans('dl-response.forum.warn.pwd_is_null'));
      return;
    }

    app.session.login(data, { errorHandler: this.onerror.bind(this) }).then(() => {
      if (this.attrs.targetUrl) {
        window.location.href = this.attrs.targetUrl;
      } else {
        window.location.reload();
      }
    }, this.loaded.bind(this));
  }

  loginParams(): LoginParams {
    const data = {
      identification: this.identification(),
      password: this.password(),
      remember: this.remember(),
    };
    return data;
  }

  onerror(error: RequestError) {
    this.loading = false;
    const sdkCode = (error.response?.errors?.[0]?.meta as { sdkCode?: number | string } | undefined)?.sdkCode;
    const translationKey = sdkCode === undefined ? undefined : sdkLoginErrorTranslations[String(sdkCode)];

    // Application shows a generic 401 alert before invoking this handler.
    app.alerts.clear();
    AlertUtil.onWarn(app, app.translator.trans(translationKey || 'dl-response.forum.login.errors.unknown'));
  }
}
